import { Link, useLocation } from 'react-router-dom';
import { Home, Grid3X3, ShoppingCart, Heart, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';

const navItems = [
  { icon: Home, label: 'Home', path: '/' },
  { icon: Grid3X3, label: 'Categories', path: '/products' },
  { icon: ShoppingCart, label: 'Cart', path: '/cart' },
  { icon: Heart, label: 'Wishlist', path: '/wishlist' },
  { icon: User, label: 'Account', path: '/profile' },
];

export function BottomNav() {
  const location = useLocation();
  const { totalItems } = useCart();
  const { wishlistCount } = useWishlist();
  const { user, setShowSignInModal } = useAuth();

  const getBadge = (path: string) => {
    if (path === '/cart' && totalItems > 0) return totalItems;
    if (path === '/wishlist' && wishlistCount > 0) return wishlistCount;
    return null;
  };

  const handleProfileClick = (e: React.MouseEvent) => {
    if (!user.isAuthenticated) {
      e.preventDefault();
      setShowSignInModal(true);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-lg lg:hidden">
      <div className="flex items-center justify-around py-2 px-2 safe-area-pb">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path === '/products' && location.pathname.startsWith('/products'));
          const badge = getBadge(item.path);
          const isProfileItem = item.path === '/profile';

          const LinkComponent = isProfileItem && !user.isAuthenticated ? 'button' : Link;
          const linkProps = isProfileItem && !user.isAuthenticated 
            ? { onClick: handleProfileClick }
            : { to: item.path };

          return (
            <LinkComponent
              key={item.path}
              {...linkProps}
              className="flex flex-col items-center gap-0.5 py-1 px-3 relative"
            >
              <div className="relative">
                <item.icon
                  className={`h-5 w-5 transition-colors ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
                />
                {badge && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-medium"
                  >
                    {badge > 9 ? '9+' : badge}
                  </motion.span>
                )}
                {/* Authentication status indicator for profile */}
                {isProfileItem && (
                  <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border border-background ${
                    user.isAuthenticated ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                )}
              </div>
              <span
                className={`text-[10px] font-medium transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {isProfileItem && !user.isAuthenticated ? 'Sign In' : item.label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                />
              )}
            </LinkComponent>
          );
        })}
      </div>
    </nav>
  );
}
